import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { UserAdminPatch } from '../../lib/types'

/** People directory — `/users`. */
export function useUsers() {
  return useQuery({ queryKey: ['users'], queryFn: () => api.users() })
}

/** Signed-in user's own profile — `/users/me`. */
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: api.me })
}

/** Оргчарт холдинга — `/users/org-chart/holding` (компании → люди; совместитель = несколько узлов). */
export function useOrgChartHolding() {
  return useQuery({ queryKey: ['org-chart-holding'], queryFn: api.orgChartHolding })
}

/** Department list with active-headcount — `/users/departments`. */
export function useDepartments() {
  return useQuery({ queryKey: ['departments'], queryFn: api.departments })
}

/** «Состояние»: отсутствующие на дату — `/users/absences?date=`. */
export function useAbsences(date: string) {
  return useQuery({ queryKey: ['absences', date], queryFn: () => api.absences(date) })
}

/** Юрлица → подразделения 1С → руководитель и люди — `/users/departments/tree`. */
export function useDepartmentTree() {
  return useQuery({ queryKey: ['department-tree'], queryFn: api.departmentTree })
}

// ── HR-редактирование карточек (роль hr/admin) ──

/** Полная кадровая карточка для префилла формы правки — `/users/:id/full`. */
export function useUserFull(id: string | null | undefined) {
  return useQuery({
    queryKey: ['user-full', id],
    queryFn: () => api.userFull(id!),
    enabled: !!id,
  })
}

/** Справочник офисов — `/locations` (для пикера локации). */
export function useLocations() {
  return useQuery({ queryKey: ['locations'], queryFn: api.locations })
}

/**
 * HR-правка чужой карточки — `PATCH /users/:id` (слать только изменённые поля).
 * Инвалидирует всё, что рисует людей: каталог, карточку, full-карточку, оргдерево, отделы.
 */
export function useUpdateUser(id: string, onDone?: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UserAdminPatch) => api.updateUser(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['user', id] })
      qc.invalidateQueries({ queryKey: ['user-full', id] })
      qc.invalidateQueries({ queryKey: ['org-chart'] })
      qc.invalidateQueries({ queryKey: ['org-chart-positions'] })
      qc.invalidateQueries({ queryKey: ['departments'] })
      onDone?.()
    },
  })
}

/** Refetch the entire HR draft before unlocking edits for a new primary. */
export function useSetPrimaryEmployment(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (employmentId: string) => {
      await api.setPrimaryEmployment(id, employmentId)
      return api.userFull(id)
    },
    onSuccess: async (full) => {
      qc.setQueryData(['user-full', id], full)
      await Promise.all(['users', 'user', 'me', 'org-chart-positions', 'departments', 'edo-employments', 'edo-route-preview', 'edo-vacation-balance', 'edo-timeoff-balance'].map(key => qc.invalidateQueries({ queryKey: key === 'user' ? [key, id] : [key] })))
    },
  })
}
